import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { addResponseMessage, toggleContactForm } from '../../store/dispatcher';
import store from '../../store';
import { GlobalState } from '../../store/types';
import closeIcon from '../../assets/close.svg';
import campaignController from '../index';
import { CampaignRenderState } from '../types';
import { greetingMessageText, senderInitial } from '../greeting';
import './style.scss';

type Props = {
  /**
   * TDS §6b "Auto-open" — `toggleConversation` from `Widget/index.tsx`, threaded through
   * `WidgetLayout`'s existing `onToggleConversation` prop (the same handle `<Launcher/>` opens
   * with). This is "the one open funnel" per the TDS precisely because it does three things a
   * hand-rolled `dispatch(toggleChat())` here could not reach: posts `isShowingChat` to the
   * loader (REQUIRED so the loader resizes the mobile wrapper off the launcher's clipped size —
   * without it "a full panel renders inside an ~80×80 box"), fires the host-provided
   * `handleToggle` callback, and stays the single place that decides open/close semantics.
   */
  onToggleConversation: () => void;
  /**
   * `Widget/index.tsx`'s `handleMessageSubmit` — the ONE composer funnel: it reports the `replied`
   * outcome, creates the session if there is none, sends the text, and echoes the visitor's own
   * bubble into the thread. The teaser's reply row hands its text to exactly that, so a reply
   * typed in the bubble is the same event as a reply typed in the panel; nothing about sessions,
   * attribution or outcomes is re-implemented here.
   */
  onSendMessage: (text: string) => void;
};

/**
 * IMBEE-8954 (T114 §6b, REQ-CWC-03/05/06) — the teaser / auto-open surface.
 *
 * Renders exactly the `'live'` outcome (teaser or auto-open) or nothing — there is no
 * fallback/offline render path, per REQ-CWC-03's "No offline/fallback state exists" scenario.
 * `idle` and `suppressed` are the SAME thing from this component's point of view: render null.
 */
function CampaignTeaser({ onToggleConversation, onSendMessage }: Props) {
  const renderState = useSelector((state: GlobalState) => state.campaign.renderState);
  const parentUrl = useSelector((state: GlobalState) => state.responses.parentUrl);
  const showChat = useSelector((state: GlobalState) => state.behavior.showChat);
  const themeColor = useSelector((state: GlobalState) => state.responses.themeColor);

  const [reply, setReply] = useState('');
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  // Whether the loader is currently holding an extended hit region / grown mobile wrapper for us.
  const surfaceReportedRef = useRef(false);

  const showBubble = renderState.status === 'teaser' && !showChat;
  const showId = renderState.status === 'idle' ? null : renderState.showId;

  /**
   * REQ-CWC-06 scenario 2 — release the extended region. The loader deliberately has no dedicated
   * release message: `isNotFocusing` (which the launcher's own `onMouseLeave` already posts)
   * clears `state.campaignSurface`. Both ways out of the teaser go through here — the visitor
   * DISMISSES it, or the visitor OPENS the chat — because otherwise the loader keeps an extended
   * hit rectangle over a region where nothing is rendered until some unrelated `mouseleave`
   * happens to fire.
   */
  const releaseSurface = useCallback(() => {
    if (!surfaceReportedRef.current) return;
    surfaceReportedRef.current = false;
    if (window.parent && parentUrl) window.parent.postMessage('isNotFocusing', parentUrl);
  }, [parentUrl]);

  // REQ-CWC-06 — the teaser reports its own footprint on mount so the loader can extend the
  // pointer-events hit region (desktop) / resize the wrapper (mobile).
  useEffect(() => {
    if (!showBubble) {
      releaseSurface();
      return;
    }
    if (!window.parent || !parentUrl) return;
    const node = bubbleRef.current;
    if (!node) return;
    const { width, height } = node.getBoundingClientRect();
    surfaceReportedRef.current = true;
    window.parent.postMessage({ type: 'IMBEE_CAMPAIGN_SURFACE', visible: true, width, height }, parentUrl);
  }, [showBubble, showId, parentUrl, releaseSurface]);

  const revealInChat = (state: Extract<CampaignRenderState, { status: 'teaser' | 'autoOpen' }>) => {
    // Idempotent in its FIRST half only: a second tap must still open the chat (the bubble comes
    // back when the visitor closes the panel), but must not add the greeting a second time. The
    // engagement lives on the controller, not in a ref here, because on mobile this
    // very call unmounts this component — `WidgetLayout` gates the teaser on
    // `!(isMobile && showChat)` and the open below sets `showChat` in the same batched commit.
    if (campaignController.getEngagedShowId() !== state.showId) {
      // AC-4.2. `showContactForm` is a TOGGLE with no setter, so this is read-then-flip: called
      // unguarded on a widget where the form is already down (member mode, a restored session) it
      // would put one back up, which is the opposite of what the campaign asked for.
      const skipsForm = state.skipPreChatForm && store.getState().behavior.showContactForm;
      // The greeting cannot be added beside the toggle: App's `[showContactForm]` effect calls
      // `dropMessages()` on exactly this transition, so a message added first is wiped one commit
      // later and the visitor gets an empty thread instead of the greeting they tapped. Handing
      // it to the controller lets App add it at the end of that same effect, after the drop —
      // which is also the only place that still exists on mobile.
      // Stash the greeting as pending in BOTH cases. On the skip path it is shown ONLY after the form
      // teardown drops messages (pending → re-added by App's effect). On the NON-skip path it is also
      // shown immediately below — but the pending copy is what survives the dropMessages the visitor's
      // LATER pre-chat-form submit fires; without it, submitting the form wiped the greeting and left an
      // empty conversation (M7, S11).
      campaignController.beginEngagement(state.showId, greetingMessageText(state.greeting));
      if (skipsForm) {
        // `toggleInputDisabled` is deliberately NOT called alongside it, unlike the three other
        // sites that take this form down: `renderContactForm`'s member-mode branch and
        // `loadSession` flip both adjacently, and the ContactForm submit flips it one level down
        // in `startWidgetSession`. App's own `[showContactForm]` effect already flips the
        // composer on this transition; all three compensate for that with a second flip on the
        // stomp-connect callback, and a campaign reveal has no session yet — so calling both here
        // would leave the composer disabled behind a form that is gone, a reply box the visitor
        // can see and cannot type in. The session the visitor's first reply creates does take the
        // composer down for its own connect, in that same funnel.
        toggleContactForm();
      } else {
        addResponseMessage(greetingMessageText(state.greeting));
      }
    }
    // Opens through the prop, never a local dispatch(toggleChat()) — see `Props` above.
    if (!store.getState().behavior.showChat) onToggleConversation();
  };

  // REQ-CWC-03 — auto-open reveals the greeting immediately, with no bubble step.
  useEffect(() => {
    if (renderState.status === 'autoOpen') revealInChat(renderState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderState.status === 'autoOpen' ? renderState.showId : null]);

  /**
   * The panel opened while a teaser was live — by the LAUNCHER, a host `customLauncher`, or anything
   * else that calls `onToggleConversation`. Reveal the same greeting the teaser click would have.
   *
   * Without this the greeting was simply lost on the most natural path there is: the visitor reads
   * the teaser, clicks the chat bubble to answer it, and the teaser unmounts (`showBubble` is false
   * once `showChat` is true) with nothing carrying its message across — a blank panel where the
   * message they were replying to had been.
   *
   * It also fixes the reporting. `beginEngagement` never ran on this path, so a visitor who DID
   * respond to the greeting was recorded as having ignored it: the show stayed `shown` with no
   * `engaged` milestone, and the engagement rate under-counted exactly the visitors who acted.
   * Treating a launcher-open under a live teaser as engagement is the judgement here — the greeting
   * is delivered into their thread either way, so the alternative is a conversation that carries a
   * campaign's greeting while its show record says the campaign was ignored.
   *
   * `revealInChat` is idempotent (it no-ops when this show is already the engaged one) and only
   * toggles the panel when it is closed, so running it here cannot double-open or double-add.
   */
  useEffect(() => {
    if (showChat && renderState.status === 'teaser') {
      releaseSurface();
      revealInChat(renderState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChat, renderState.status === 'teaser' ? renderState.showId : null]);

  if (!showBubble || renderState.status !== 'teaser') return null;

  const { greeting } = renderState;

  const handleOpen = () => {
    releaseSurface();
    revealInChat(renderState);
  };

  // REQ-CWC-05 — a dismissal without a reply. It also ENDS the engagement when this is the show
  // the visitor had revealed; the one rule is on `campaignController`'s `engagedShowId`.
  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    releaseSurface();
    campaignController.reportOutcome('dismissed');
  };

  /**
   * REQ-CWC-05 / AC-4.1 — the visitor replies from the bubble itself.
   *
   * The reply reveals the greeting in the panel FIRST and sends second, in that order: the visitor
   * must land in a thread that already contains what they are answering, and `revealInChat` is
   * also what opens the panel and registers the engagement the send's `replied` outcome is
   * attributed to. Sending first would attribute a reply to a show that had never been revealed.
   */
  const handleReply = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const text = reply.trim();
    if (!text) return;
    setReply('');
    releaseSurface();
    // Read BEFORE the reveal: the reveal is what takes a skipped form down.
    const formIsUp = store.getState().behavior.showContactForm;
    revealInChat(renderState);
    if (formIsUp) {
      // The form is coming down — now, if the campaign skips it, or on the visitor's own submit
      // if it does not. Either way the session belongs to that transition, so the reply waits for
      // it on the controller and `App` sends it there. Sending here would post with an empty
      // session id, because `onSendMessage` closes over the pre-teardown `isShowingForm`.
      campaignController.parkPendingReply(text);
    } else {
      onSendMessage(text);
    }
  };

  return (
    <div className="campaign-teaser" data-testid="campaign-teaser" ref={bubbleRef} onClick={handleOpen}>
      <button type="button" className="campaign-teaser-dismiss" aria-label="Dismiss" onClick={handleDismiss}>
        <img src={closeIcon} alt="" />
      </button>
      <div className="campaign-teaser-top">
        {/* The mockup draws a sender circle whenever there IS a sender, and the API's
            `sender.avatarUrl` is optional — this env's own `live-chat-campaign-shows` answers
            `sender: {"name": "…"}` with no photo at all, so rendering the avatar only when a URL
            exists drew nothing for the common case and left the bubble visually headless. The
            initial is therefore derived from the name.

            The name itself is OPTIONAL (PRD AC-1.1): an unnamed greeting renders with NO
            attribution — avatar and name row dropped TOGETHER, never a blank circle above a blank
            bold line. Previously an unnamed campaign never rendered at all (S11b B3). */}
        {greeting.senderName &&
          (greeting.avatarUrl ? (
            <img className="campaign-teaser-avatar" src={greeting.avatarUrl} alt="" data-testid="campaign-teaser-avatar" />
          ) : (
            <div
              className="campaign-teaser-avatar campaign-teaser-avatar-fallback"
              aria-hidden="true"
              data-testid="campaign-teaser-avatar-fallback"
            >
              {senderInitial(greeting.senderName)}
            </div>
          ))}
        <div className="campaign-teaser-body">
          {greeting.senderName && <div className="campaign-teaser-sender">{greeting.senderName}</div>}
          <div className="campaign-teaser-title">{greeting.title}</div>
          <div className="campaign-teaser-message" style={themeColor ? { borderColor: `#${themeColor}` } : undefined}>
            {greeting.body}
          </div>
        </div>
      </div>
      {/* The mockup's `.pop-reply` row, docked inside the bubble — state A's own caption reads
          "visitor can reply or dismiss". Without it the whole bubble is a single open-the-panel
          target, which is a materially different interaction: the visitor cannot answer where they
          were asked, only travel to somewhere they can. */}
      <form className="campaign-teaser-reply" onSubmit={handleReply} onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          className="campaign-teaser-reply-input"
          aria-label="Reply to start chatting"
          placeholder="Reply to start chatting…"
          value={reply}
          data-testid="campaign-teaser-reply-input"
          onChange={(e) => setReply(e.target.value)}
        />
        <button
          type="submit"
          className="campaign-teaser-reply-send"
          aria-label="Send"
          disabled={reply.trim() === ''}
          data-testid="campaign-teaser-reply-send"
        >
          <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
            <path d="M4 12l16-8-6 16-3-6-7-2z" fill="currentColor" />
          </svg>
        </button>
      </form>
    </div>
  );
}

export default CampaignTeaser;
